import React from 'react';
import PropTypes from 'prop-types';
import { DragSource } from 'react-dnd';
import moment from 'moment';
import { grey700 } from 'material-ui/styles/colors';
import { ragStatusColors } from '../../../settings/other';
import globals from '../../../globals/communityGlobals';
import TreatmentInstancePopover from './../TreatmentInstancePopover/TreatmentInstancePopover';
import TreatmentInstanceDialog from '../../../containers/DailyPlanning/TreatmentInstanceDialog/TreatmentInstanceDialogContainer';
import OverrideClinician from '../OverrideClinician/OverrideClinician';
import { refSetter } from '../../../utils/helpers';
import { cardTypes } from '../../../settings/constants';
import gradePropTypes from '../../../propTypes/entities/grade';

const locale = globals.getArea('dailyPlanning');
const treatmentslocal = globals.getArea('treatments');

export const TREATMENTINSTANCE = 'TREATMENTINSTANCE';

const treatmentInstanceSource = {
  isDragging(props, monitor) {
    const { treatmentInstanceId, treatmentId, occurrence } = monitor.getItem();
    return (
      treatmentInstanceId === props.treatmentInstanceId &&
      treatmentId === props.treatmentId &&
      occurrence === props.occurrence
    );
  },

  beginDrag(
    {
      assignGuid,
      requiredGrade,
      requiredSkills,
      requiredTime,
      effectiveTimeSlots,
      cardType,
      location,
      treatmentId,
      treatmentInstanceId,
      occurrence,
      getTravelTimesByPostcode,
      getPostcodeDistance,
      isSpecificTime,
      masterId,
      isMasterAssigned,
      excludedClinicians,
    },
    monitor,
    component,
  ) {
    getTravelTimesByPostcode(location);
    getPostcodeDistance(location);
    return {
      assignGuid,
      requiredGrade,
      requiredSkills,
      requiredTime,
      cardType,
      treatmentId,
      treatmentInstanceId,
      effectiveTimeSlots,
      occurrence,
      previewHtml: component.preview.outerHTML,
      previewWidth: component.preview.getBoundingClientRect().width,
      schedulingDisabled: false,
      isSpecificTime,
      masterId,
      location,
      isMasterAssigned,
      excludedClinicians,
    };
  },

  async endDrag(props, monitor) {
    if (!monitor.didDrop()) {
      return;
    }
    const {
      treatmentId,
      treatmentInstanceId,
      assignGuid,
      cardType,
      occurrence,
      effectiveTimeSlots,
    } = monitor.getItem();
    const { postingId, startDate } = monitor.getDropResult();
    const { jwtToken } = props;
    const doesHaveCopies = props.copiesCount > 0;
    const isMaster = props.cardType === cardTypes.MASTER;

    try {
      const result = await props.assignTreatment({
        postingId,
        treatmentId,
        treatmentInstanceId,
        assignmentTime: startDate,
        cardType,
      });
      if (result.response.status === 200) {
        props.refreshScheduler(
          assignGuid,
          result.response.data.newAssignmentId,
          postingId,
          startDate,
          jwtToken,
          props.masterId,
        );
        props.getTeamAvailability(props.teamId, startDate, jwtToken, props.requiredTime);
        if (isMaster) {
          props.removeTreatmentInstance(treatmentId, assignGuid, occurrence, effectiveTimeSlots);
        } else {
          props.removeCopyTreatmentInstance(assignGuid);
        }
        if (doesHaveCopies) {
          props.getTreatmentInstancesCopies(result.response.data.newAssignmentId, props.teamId, jwtToken);
        }
      }
    } catch (error) {
      // Log errors
      // eslint-disable-next-line no-console
      console.error(error.response ? error.response.data : error);
    }
  },
};

function collect(connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  };
}

class TreatmentInstance extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      open: false,
    };
    const bgColor = ragStatusColors[this.props.ragStatus.toLowerCase()];
    this.cardStatusBgColor = { backgroundColor: bgColor };
    this.dateCardStyle = { backgroundColor: bgColor, float: 'left', marginRight: '5px' };
  }

  shouldComponentUpdate = nextProps => {
    const { isFirstChild, isLastChild, isMasterAssigned } = this.props;
    return (
      isFirstChild !== nextProps.isFirstChild ||
      isLastChild !== nextProps.isLastChild ||
      isMasterAssigned !== nextProps.isMasterAssigned
    );
  };

  shouldComponentUpdate = nextProps => this.props.assignGuid !== nextProps.assignGuid;

  setRef = refSetter.bind(this);

  generateActivitiesNames = activities => activities.map(activity => activity.name).join(', ');

  formatHour = date =>
    moment(date)
      .utcOffset(0, true)
      .format(globals.timeFormats.short);

  handleTouchTap = event => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      open: true,
      anchorEl: event.currentTarget,
    });
  };

  handleRequestClose = () => {
    this.setState({
      open: false,
    });
  };

  isReview = this.props.cardType === cardTypes.REVIEW;

  overrideClinician = () => {
    const { treatmentId, treatmentInstanceId, occurrence, timeSlots, overrideClinician } = this.props;
    overrideClinician(treatmentId, treatmentInstanceId, occurrence, timeSlots);
  };

  removeOverrideClinician = () => {
    const { treatmentInstanceId, removeOverrideClinician } = this.props;
    removeOverrideClinician(treatmentInstanceId);
  };

  render() {
    const {
      patientName,
      requiredTime,
      nhsNumber,
      activities,
      requiredGrade,
      location,
      ragStatus,
      connectDragSource,
      isDragging,
      canAllocateWork,
      timeSlots = [],
      cardType,
      copiesCount,
      wasUnassignedBySynchronization,
      isSingleTimeSlot,
      uniqueTreatmentId,
      treatmentInstanceId,
      treatmentId,
      occurrence,
      isMasterAssigned,
      isFirstChild,
      isLastChild,
      additionalCliniciansCount,
      isTwilightTreatment,
      hasVarianceFactorChanged,
    } = this.props;
    const bgColor = ragStatusColors[ragStatus.toLowerCase()];
    const infoText = locale.getMsg('information');
    const treatmentSlotsText = locale.getMsg('treatmentSlots');
    const tooltipContent = (
      <div className="clinician-info-planning">
        {[
          <div key={`tooltipTreatmentInstanceHeader${uniqueTreatmentId}`}>
            <h5 className="popover-title">{infoText}</h5>
            <span className="title-sub">{treatmentSlotsText}</span>
          </div>,
          timeSlots.map(({ id, from, to, name }) => {
            const toolTipKey = `tooltipTreatmentInstanceTimeSlot${uniqueTreatmentId + id}`;
            const toolTipText = `${name ? `${name}: ` : ''}${this.formatHour(from) +
              (from === to ? '' : ` - ${this.formatHour(to)}`)}`;
            return (
              <span key={toolTipKey} className="info-timeslot">
                {toolTipText}
              </span>
            );
          }),
        ]}
      </div>
    );
    let masterChildIcon;
    let cardClass;
    let gradeRow;

    if (cardType === cardTypes.MASTER || this.isReview) {
      masterChildIcon =
        additionalCliniciansCount > 0 ? (
          <span className="treatmentlist_icon">
            <i className="allocateIcons">&#xE7FD;</i>
            <span>{`+${additionalCliniciansCount}`}</span>
          </span>
        ) : (
          <span className="treatmentlist_icon">
            <i className="allocateIcons">person</i>
          </span>
        );
      cardClass = copiesCount > 0 ? 'card card-master' : 'card card-master-nocopies';
      gradeRow = <span className="grade">{requiredGrade.name}</span>;
    } else {
      masterChildIcon = (
        <span className="treatmentlist_icon">
          <i className="allocateIcons">person_outline</i>
          {cardType === cardTypes.OVERRIDDEN_CLINICIAN ? ' ORC' : ''}
        </span>
      );
      gradeRow = <span className="grade" />;

      let copyPositionClass = '';
      if (isFirstChild && isLastChild) {
        copyPositionClass = 'single';
      } else if (isFirstChild) {
        copyPositionClass = 'first';
      } else if (isLastChild) {
        copyPositionClass = 'last';
      } else {
        copyPositionClass = 'middle';
      }

      cardClass = `card card-copy ${copyPositionClass}`;

      if (!isMasterAssigned) {
        cardClass += ' withmaster';
      }
    }

    let twilight;

    if (isTwilightTreatment === true) {
      twilight = <i className="allocateIcons">brightness_2</i>;
    }

    let timeSlotLabel = `${this.formatHour(timeSlots[0].from)} - ${this.formatHour(timeSlots[0].to)}`;
    if (!isSingleTimeSlot) {
      timeSlotLabel = locale.getMsg('multipleTimeSlots');
    } else if (timeSlots[0].from === timeSlots[0].to) {
      timeSlotLabel = this.formatHour(timeSlots[0].from);
    }

    const isUnassignedBySyncStyle = wasUnassignedBySynchronization ? 'sync-unassignment' : 'sync-unassignment-hidden';
    const isTwilightTreatmentStyle =
      isTwilightTreatment === true
        ? { backgroundColor: grey700, float: 'right' }
        : { backgroundColor: bgColor, float: 'right' };
    const requiredTimeText = `${requiredTime} min`;
    const hasVarianceFactorChangedText = hasVarianceFactorChanged ? ` ${treatmentslocal.getMsg('avfLabel')}` : '';
    const cardDummyContentStyle = `card-content card-dummy ${isDragging ? '' : 'card-hidden'}`;
    const cardStatusStyle = `card-status ${isDragging ? 'card-hidden' : ''}`;
    const cardContentStyle = `card-content ${isDragging ? 'card-hidden' : ''}`;
    const activitiesNamesList = this.generateActivitiesNames(activities);
    const unassignedDuringSynchronizationText = locale.getMsg('unassignedDuringSynchronization');
    const reviewText = locale.getMsg('review');
    const previewRef = this.setRef('preview');
    const cardContent = (
      <div ref={previewRef} className={cardClass}>
        <div className={cardDummyContentStyle} />
        <div className={cardStatusStyle} style={this.cardStatusBgColor} />
        <div className={cardContentStyle}>
          <div className="card-row">
            {gradeRow}
            <div className={isUnassignedBySyncStyle}>{unassignedDuringSynchronizationText}</div>
            <div>
              <div className="required-timeframe footer_icons" style={isTwilightTreatmentStyle}>
                <span className="timeframe_icon">
                  {twilight}
                  <span>{timeSlotLabel}</span>
                </span>
              </div>
            </div>
          </div>
          <div className="card-row">
            <p className="patient-name">{patientName}</p>
          </div>
          <div className="card-row">
            <p>{`(${nhsNumber})`}</p>
            {this.isReview && <span className="review">{reviewText}</span>}
          </div>
          <div className="card-row">
            <p className="activities">{activitiesNamesList}</p>
          </div>
          <div className="card-row">
            <div className="footer_icons">
              <span className="treatmentlist_icon">
                <i className="allocateIcons">location_on</i>
                <span>{location}</span>
              </span>
              <span className="treatmentlist_icon">
                <i className="allocateIcons">timer</i>
                <span>{requiredTimeText}</span>
                {hasVarianceFactorChangedText}
              </span>
              {masterChildIcon}
            </div>
            <div className="footer_icons">
              {canAllocateWork === true ? (
                <OverrideClinician
                  occurrence={occurrence}
                  cardType={cardType}
                  copiesCount={copiesCount}
                  handleAdd={this.overrideClinician}
                  handleRemove={this.removeOverrideClinician}
                />
              ) : (
                <div />
              )}
              <TreatmentInstancePopover
                id={`${uniqueTreatmentId}Dialog`}
                treatmentId={treatmentId}
                treatmentInstanceId={treatmentInstanceId}
                occurrence={occurrence}
              >
                {tooltipContent}
              </TreatmentInstancePopover>
              <TreatmentInstanceDialog
                id={`${uniqueTreatmentId}Dialog`}
                dateAt={occurrence}
                treatmentId={treatmentId}
                treatmentInstanceId={treatmentInstanceId}
                cardType={cardType}
              />
            </div>
          </div>
        </div>
      </div>
    );
    return canAllocateWork ? connectDragSource(cardContent) : cardContent;
  }
}

TreatmentInstance.propTypes = {
  occurrence: PropTypes.string.isRequired,
  patientName: PropTypes.string.isRequired,
  nhsNumber: PropTypes.string.isRequired,
  requiredTime: PropTypes.number.isRequired,
  canAllocateWork: PropTypes.bool.isRequired,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      activityId: PropTypes.number,
      duration: PropTypes.number,
    }),
  ).isRequired,
  overrideClinician: PropTypes.func.isRequired,
  removeOverrideClinician: PropTypes.func.isRequired,
  // Used for drag
  /* eslint-disable react/no-unused-prop-types */
  assignGuid: PropTypes.number.isRequired,
  requiredSkills: PropTypes.arrayOf(PropTypes.number).isRequired,
  requiredGrade: gradePropTypes.isRequired,
  timeSlots: PropTypes.arrayOf(
    PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
    }),
  ).isRequired,
  isSingleTimeSlot: PropTypes.bool.isRequired,
  location: PropTypes.string.isRequired,
  ragStatus: PropTypes.string.isRequired,
  cardType: PropTypes.string.isRequired,
  copiesCount: PropTypes.number.isRequired,
  additionalCliniciansCount: PropTypes.number.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  wasUnassignedBySynchronization: PropTypes.bool.isRequired,
  uniqueTreatmentId: PropTypes.string.isRequired,
  // Used for drag and drop
  /* eslint-disable react/no-unused-prop-types */
  treatmentInstanceId: PropTypes.number.isRequired,
  treatmentId: PropTypes.number.isRequired,
  isFirstChild: PropTypes.bool.isRequired,
  isLastChild: PropTypes.bool.isRequired,
  isMasterAssigned: PropTypes.bool.isRequired,
  isTwilightTreatment: PropTypes.bool.isRequired,
  hasVarianceFactorChanged: PropTypes.bool.isRequired,
  teamId: PropTypes.number.isRequired,
  jwtToken: PropTypes.string.isRequired,
  /* eslint-enable */
};

export default DragSource(TREATMENTINSTANCE, treatmentInstanceSource, collect)(TreatmentInstance);
